/**
 * CustomerTierCard — Feb 2026 Hybrid Tier Model
 *
 * Shows a customer their loyalty tier + progress along BOTH paths
 * (annual delivered orders OR annual earned points). The path they're
 * currently qualifying on gets a Sparkles accent + border-glow.
 *
 * ┌──────────────────────────────────────────┐
 * │  🥈 SILVER  · qualifying via POINTS     │
 * │                                          │
 * │  ORDERS  ▓▓░░░░░░░░  8 / 10             │
 * │  POINTS  ▓▓▓▓▓▓▓▓▓░  12,500 / 15,000    │ ← highlighted row
 * │                                          │
 * │  Next: GOLD  (2 more orders or 2,500 pts)│
 * └──────────────────────────────────────────┘
 *
 * Theme-aware — all colors, radii, shadows come from CSS variables
 * scoped by `[data-theme="modern"]`. The `code` theme uses hardcoded
 * neon-terminal values (backward compat with the existing dashboard).
 *
 * Props (from `/api/users/me/tier`):
 *   tier            'standard'|'silver'|'gold'|'platinum'|'onyx'
 *   annualOrders    int  — delivered orders this year
 *   annualPoints    int  — earned points this year (admin_adjust excluded)
 *   toNextOrders    int|null — orders still needed for next tier
 *   toNextPoints    int|null — pts still needed for next tier
 *   nextTier        string|null
 *   qualifyingPath  'orders'|'points'|'both'|null
 *   pointsBalance   int
 *   isLocked        bool — whether an SA lock is active
 *
 * Presentational only — the parent (`CustomerDashboard`) does the fetch.
 */

import { Lock, Sparkles, Crown, Award, Leaf, Package, Coins, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTierPerks } from '../hooks/useTierPerks';

// Tier metadata — matches customer_tier.py labels + hex accents. Kept in
// sync with TIER_LABELS / TIER_COLORS on the backend.
const TIER_META = {
  standard: {
    label: 'NEW',
    headline: "Let's get you started",
    color: '#8E8E93',
    modernColor: '#5F6368', // deep variant, AA contrast on light canvas
    icon: Leaf,
    perks: ['Earn 1× on every delivered order', 'Welcome bonus'],
    ordersThreshold: 0,
    pointsThreshold: 0,
    referralsThreshold: 0,
  },
  silver: {
    label: 'SILVER',
    headline: 'Welcome to SILVER',
    color: '#C0C0C0',
    modernColor: '#5B6470', // deep variant, AA contrast on light canvas
    icon: Award,
    perks: ['Earn 1.25× on every order', '$5 birthday bonus points'],
    ordersThreshold: 3,
    pointsThreshold: 5000,
    referralsThreshold: 0,       // disabled on silver
  },
  gold: {
    label: 'GOLD',
    headline: "You're GOLD",
    color: '#FFD700',
    modernColor: '#8A6A1F', // deep variant, AA contrast on light canvas
    icon: Award,
    perks: ['Earn 1.5× on every order', 'Priority in-app perks'],
    ordersThreshold: 10,
    pointsThreshold: 15000,
    referralsThreshold: 5,
  },
  platinum: {
    label: 'PLATINUM',
    headline: "You're PLATINUM",
    color: '#E5E4E2',
    modernColor: '#4A5A6A', // deep variant, AA contrast on light canvas
    icon: Sparkles,
    perks: ['Earn 1.75× on every order', 'Priority handling'],
    ordersThreshold: 25,
    pointsThreshold: 40000,
    referralsThreshold: 10,
  },
  onyx: {
    label: 'ONYX',
    headline: "You're ONYX",
    color: '#BF5AF2',
    modernColor: '#6B2FA3', // deep variant, AA contrast on light canvas
    icon: Crown,
    perks: ['Earn 2.0× on every order', 'Concierge support'],
    ordersThreshold: 50,
    pointsThreshold: 100000,
    referralsThreshold: 15,
  },
  black_card: {
    label: 'BLACK CARD',
    headline: "Welcome, Black Card",
    color: '#111111',
    modernColor: '#111111', // deep variant, AA contrast on light canvas
    icon: Crown,
    perks: ['Earn 2.5× on every order', 'Always-on free delivery', 'Concierge'],
    ordersThreshold: 999,
    pointsThreshold: 9999999,
    referralsThreshold: 999,
  },
};

// Fallback for unknown tier keys (e.g. legacy 'new'/'regular') — degrade
// gracefully rather than crash the card.
// Modern-only brushed-metal gradients (large surfaces). Small text keeps solid modernColor.
const METAL = {
  silver:     'linear-gradient(135deg, #4A535D 0%, #7D8792 24%, #C9D0D7 44%, #8E98A2 62%, #4F5862 84%, #7A848F 100%)',
  gold:       'linear-gradient(135deg, #6A4A0F 0%, #A57A22 24%, #DDB453 44%, #B98A2A 62%, #74560F 84%, #A07A26 100%)',
  // Platinum = flagship VIP tier: iridescent pearl-platinum (ice blue / lavender / champagne flashes).
  platinum:   'linear-gradient(120deg, #2F3A4A 0%, #5C6B84 16%, #9AA9C4 30%, #C9B8DE 40%, #A9CFE3 50%, #E2C9B8 58%, #8C9CBB 68%, #3B4659 84%, #66758F 100%)',
  // Onyx = black pearl: light iridescent lettering on an obsidian card (violet / teal / rose flashes).
  onyx:       'linear-gradient(120deg, #A58BD8 0%, #EADCFF 20%, #8FDDD6 36%, #F3BFE8 52%, #CDB8F5 66%, #9677D2 84%, #E0D0FB 100%)',
  black_card: 'linear-gradient(135deg, #000000 0%, #2B2B2B 30%, #6A6A6A 46%, #262626 62%, #000000 100%)',
};
// Lighter iridescent ring for Platinum borders (the text gradient is too dark at the edges).
const PEARL_RING = 'linear-gradient(120deg, #8E9DBA 0%, #C9B8DE 25%, #A9CFE3 50%, #E2C9B8 75%, #8E9DBA 100%)';
const ONYX_RING = 'linear-gradient(120deg, #6E4FA8 0%, #C9B3F2 25%, #6FC7C0 50%, #E6A8D8 75%, #6E4FA8 100%)';
const GEM = {
  platinum: { cls: 'am-tier-pearl', ring: PEARL_RING, fill: 'linear-gradient(160deg, #FFFFFF 0%, #F5F2FB 38%, #EEF5FA 68%, #FBF7F3 100%)', rowBg: '#FFFFFF',
    grain: 'radial-gradient(circle at 30% 30%, #E7DCF5 0%, #D6EAF5 45%, #F3E6DA 75%, transparent 100%)' },
  onyx:     { cls: 'am-tier-blackpearl', ring: ONYX_RING, fill: 'linear-gradient(160deg, #1B1326 0%, #0F0B16 55%, #1C1429 100%)', rowBg: '#16101F',
    grain: 'radial-gradient(circle at 30% 30%, rgba(201,179,242,0.55) 0%, rgba(111,199,192,0.35) 45%, rgba(230,168,216,0.25) 75%, transparent 100%)' },
};
const SHIMMER_TIERS = new Set(['gold', 'platinum', 'onyx', 'black_card']);

function metaFor(key, perksMap) {
  const m = TIER_META[key] || TIER_META.standard;
  const perks = (perksMap && perksMap[key]) || m.perks.map((text) => ({ text }));
  return { ...m, perks };
}

export function PerkText({ perk }) {
  return (
    <span>
      {perk.text}
      {perk.coming_soon && <span className="am-perk-soon" data-testid="perk-coming-soon">COMING SOON</span>}
    </span>
  );
}

export const TIER_ORDER = ['silver', 'gold', 'platinum', 'onyx'];
export { metaFor as tierMeta };

// Visual tokens for one tier in the current theme (shared by the card + showcase).
export function tierLook(tier, isModern, perksMap = null) {
  const t = metaFor(tier, perksMap);
  const gem = isModern ? GEM[tier] : null;
  const tc = gem && tier === 'onyx' ? '#D9C2F5' : (isModern ? t.modernColor : t.color);
  const metal = isModern ? METAL[tier] : null;
  return { t, gem, tc, metal, shimmer: !!metal && SHIMMER_TIERS.has(tier) };
}

export function TierCardFrame({ look, isModern, testid, className = '', children }) {
  const { gem, tc, metal } = look;
  return (
    <div
      data-testid={testid}
      className={`${isModern ? `am-modern-scope am-tier-card-modern ${metal ? 'am-tier-metal' : ''} ${gem ? `am-tier-gem ${gem.cls}` : ''}` : 'am-tier-card-code'} ${className}`}
      style={metal
        ? { borderColor: 'transparent', background: `${gem ? gem.fill : 'linear-gradient(var(--am-card), var(--am-card))'} padding-box, ${gem ? gem.ring : metal} border-box` }
        : { borderColor: tc }}
    >
      <div aria-hidden="true" className="am-tier-grain" style={{ background: gem ? gem.grain : tc }} />
      {gem && <div aria-hidden="true" className="am-pearl-foil" />}
      <div className="am-tier-inner">{children}</div>
    </div>
  );
}

export function TierCardHeader({ look, isModern, eyebrow, sub }) {
  const { t, gem, tc, metal, shimmer } = look;
  const Icon = t.icon;
  return (
    <div className="am-tier-header">
      <div>
        <div className="am-tier-eyebrow" style={{ color: tc }}>
          <span className="am-tier-eyebrow-dot" style={{ background: tc }} aria-hidden="true" />
          <span data-testid="tier-eyebrow-label">{eyebrow}</span>
        </div>
        <h2
          data-testid="tier-headline"
          className={`am-tier-headline ${metal ? 'am-metal-text' : ''} ${shimmer ? 'am-metal-shimmer' : ''}`}
          style={metal ? { backgroundImage: metal } : { color: tc, textShadow: isModern ? 'none' : `0 0 18px ${tc}33` }}
        >
          {t.label}
        </h2>
        <p className="am-tier-sub">{sub === undefined ? t.headline : sub}</p>
      </div>
      <div
        className={`am-tier-icon ${metal ? 'am-metal-tile' : ''}`}
        style={metal ? { background: metal, borderColor: 'transparent', color: '#FFFFFF' } : { borderColor: tc, color: tc }}
        aria-hidden="true"
      >
        <Icon className="am-tier-icon-svg" />
        {gem && (
          <>
            <span className="am-twinkle" style={{ top: 6, right: 7 }} />
            <span className="am-twinkle" style={{ bottom: 8, left: 7, animationDelay: '1.4s' }} />
          </>
        )}
      </div>
    </div>
  );
}

// Segmented progress bar. Uses CSS vars so it re-skins with the theme.
// `progress` is 0-1. Emerald in Modern theme, current-tier neon in Code.
function ProgressBar({ progress, accent, fill, testId, highlighted }) {
  const pct = Math.max(0, Math.min(1, Number(progress) || 0)) * 100;
  return (
    <div
      data-testid={testId}
      className="am-tier-bar-track"
      style={{
        // Track outline in Modern uses hairline; falls back to neon border in Code.
        border: `1px solid var(--am-tier-track-border, ${accent}55)`,
        background: 'var(--am-tier-track-bg, #0C0612)',
        borderRadius: 'var(--am-radius-xs, 0)',
      }}
    >
      <div
        className="am-tier-bar-fill"
        style={{
          width: `${pct}%`,
          background: fill || accent,
          opacity: fill ? (highlighted ? 1 : 0.8) : (highlighted ? 0.95 : 0.55),
          borderRadius: 'var(--am-radius-xs, 0)',
        }}
      />
      {/* 10 gauge segments — kept subtle enough to disappear in Modern */}
      <div className="am-tier-bar-segments" aria-hidden="true">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} />
        ))}
      </div>
    </div>
  );
}

export function CustomerTierCard({
  tier = 'standard',
  annualOrders = 0,
  annualPoints = 0,
  annualReferrals = 0,
  toNextOrders = null,
  toNextPoints = null,
  toNextReferrals = null,
  nextTier = null,
  qualifyingPath = null,
  pointsBalance = 0,
  isLocked = false,
  hideNextPerks = false,
}) {
  const { isModern } = useTheme();
  const perksMap = useTierPerks();
  const look = tierLook(tier, isModern, perksMap);
  const { t, gem, tc, metal } = look;
  const next = nextTier ? metaFor(nextTier, perksMap) : null;
  const nc = next ? (isModern ? next.modernColor : next.color) : null;

  // Progress fractions per path (0-1). At the top tier, bars max out.
  const ordersProgress = (() => {
    if (!next) return 1;
    const denom = next.ordersThreshold - t.ordersThreshold;
    if (denom <= 0) return 0;
    return Math.min(1, Math.max(0, annualOrders - t.ordersThreshold) / denom);
  })();
  const pointsProgress = (() => {
    if (!next) return 1;
    const denom = next.pointsThreshold - t.pointsThreshold;
    if (denom <= 0) return 0;
    return Math.min(1, Math.max(0, annualPoints - t.pointsThreshold) / denom);
  })();
  const referralsProgress = (() => {
    if (!next || !next.referralsThreshold) return 0;
    const denom = next.referralsThreshold - (t.referralsThreshold || 0);
    if (denom <= 0) return 0;
    return Math.min(1, Math.max(0, annualReferrals - (t.referralsThreshold || 0)) / denom);
  })();

  // Which row(s) get the Sparkles accent + border-glow.
  const highlightOrders = qualifyingPath === 'orders' || qualifyingPath === 'both' || qualifyingPath === 'all';
  const highlightPoints = qualifyingPath === 'points' || qualifyingPath === 'both' || qualifyingPath === 'all';
  const highlightReferrals = qualifyingPath === 'referrals' || qualifyingPath === 'all';

  // Matte Gold for the referrals row — always, per spec.
  const refAccent = tier === 'onyx' && gem ? '#E3C77A' : (isModern ? '#8A6A1F' : '#D4AF37');
  const refFill = isModern ? METAL.gold : null;
  // Platinum highlighted rows get an iridescent hairline instead of a flat slate border.
  const rowStyle = (on) => (metal && on
    ? { borderColor: 'transparent', background: `linear-gradient(${gem ? gem.rowBg : '#FFFFFF'}, ${gem ? gem.rowBg : '#FFFFFF'}) padding-box, ${gem ? gem.ring : metal} border-box` }
    : { borderColor: on ? tc : 'transparent' });

  return (
    <TierCardFrame look={look} isModern={isModern} testid="customer-tier-card">
        <TierCardHeader look={look} isModern={isModern} eyebrow={isLocked ? 'YOUR LOCKED-IN TIER' : 'YOUR TIER'} />

        {/* ─── Qualifying-path chip ─────────────────────────── */}
        {qualifyingPath && (
          <div
            data-testid="tier-qualifying-chip"
            className="am-tier-chip"
            style={{
              borderColor: `${tc}55`,
              background: `${tc}12`,
              color: tc,
            }}
          >
            <Sparkles className="am-tier-chip-icon" />
            <span>
              {qualifyingPath === 'all'
                ? 'Qualifying on all three paths'
                : qualifyingPath === 'both'
                ? 'Qualifying on multiple paths'
                : `Qualifying via ${qualifyingPath}`}
            </span>
          </div>
        )}

        {/* ─── Progress rows: side-by-side paths ─────────────── */}
        {next && (
          <div className="am-tier-progress-block">
            {/* Orders row */}
            <div
              data-testid="tier-orders-row"
              className={`am-tier-row ${highlightOrders ? 'is-highlighted' : ''}`}
              style={rowStyle(highlightOrders)}
            >
              <div className="am-tier-row-header">
                <span className="am-tier-row-label">
                  <Package className="am-tier-row-icon" aria-hidden="true" />
                  <span>Orders path</span>
                </span>
                <span className="am-tier-row-stat" data-testid="tier-orders-stat">
                  <span className="am-tier-row-cur">{annualOrders.toLocaleString()}</span>
                  <span className="am-tier-row-slash"> / </span>
                  <span className="am-tier-row-goal">{next.ordersThreshold}</span>
                </span>
              </div>
              <ProgressBar
                progress={ordersProgress}
                accent={tc}
                fill={metal}
                testId="tier-orders-progress"
                highlighted={highlightOrders}
              />
            </div>

            {/* Points row */}
            <div
              data-testid="tier-points-row"
              className={`am-tier-row ${highlightPoints ? 'is-highlighted' : ''}`}
              style={rowStyle(highlightPoints)}
            >
              <div className="am-tier-row-header">
                <span className="am-tier-row-label">
                  <Coins className="am-tier-row-icon" aria-hidden="true" />
                  <span>Points path</span>
                </span>
                <span className="am-tier-row-stat" data-testid="tier-points-stat">
                  <span className="am-tier-row-cur">{annualPoints.toLocaleString()}</span>
                  <span className="am-tier-row-slash"> / </span>
                  <span className="am-tier-row-goal">{next.pointsThreshold.toLocaleString()}</span>
                </span>
              </div>
              <ProgressBar
                progress={pointsProgress}
                accent={tc}
                fill={metal}
                testId="tier-points-progress"
                highlighted={highlightPoints}
              />
            </div>

            {/* Referrals row — Matte Gold accent, always */}
            {next.referralsThreshold > 0 && (
              <div
                data-testid="tier-referrals-row"
                className={`am-tier-row ${highlightReferrals ? 'is-highlighted' : ''}`}
                style={{
                  borderColor: highlightReferrals ? refAccent : 'transparent',
                  color: refAccent,
                }}
              >
                <div className="am-tier-row-header">
                  <span className="am-tier-row-label" style={{ color: refAccent }}>
                    <Users className="am-tier-row-icon" aria-hidden="true" />
                    <span>Referrals path</span>
                  </span>
                  <span className="am-tier-row-stat" data-testid="tier-referrals-stat">
                    <span className="am-tier-row-cur">{annualReferrals.toLocaleString()}</span>
                    <span className="am-tier-row-slash"> / </span>
                    <span className="am-tier-row-goal">{next.referralsThreshold}</span>
                  </span>
                </div>
                <ProgressBar
                  progress={referralsProgress}
                  accent={refAccent}
                  fill={refFill}
                  testId="tier-referrals-progress"
                  highlighted={highlightReferrals}
                />
              </div>
            )}
          </div>
        )}

        {/* ─── Next-tier copy or top-tier badge ─────────────── */}
        {next ? (
          <p className="am-tier-next" data-testid="tier-next-copy">
            <span className="am-tier-next-prefix">Next</span>
            <span className="am-tier-next-arrow" aria-hidden="true">→</span>
            <span className="am-tier-next-label" style={{ color: nc }}>{next.label}</span>
            <span className="am-tier-next-detail">
              {toNextOrders != null && toNextPoints != null && (
                <>
                  {' '}
                  ({toNextOrders} more order{toNextOrders !== 1 ? 's' : ''}
                  {' or '}
                  {toNextPoints.toLocaleString()} more pts
                  {toNextReferrals != null && next.referralsThreshold > 0 && (
                    <>
                      {' or '}
                      {toNextReferrals} more referral{toNextReferrals !== 1 ? 's' : ''}
                    </>
                  )}
                  )
                </>
              )}
            </span>
          </p>
        ) : (
          <div
            data-testid="tier-top-badge"
            className="am-tier-top"
            style={{ borderColor: tc, color: tc, background: `${tc}0F` }}
          >
            ◆ TOP TIER UNLOCKED
          </div>
        )}

        {/* ─── Perks summary ────────────────────────────────── */}
        <div className="am-tier-perks">
          <div className="am-tier-perks-heading">YOUR PERKS</div>
          <ul>
            {t.perks.map((p, i) => (
              <li key={i} data-testid={`tier-perk-${i}`}>
                <span className="am-tier-perk-mark" style={{ color: tc }}>▸</span>
                <PerkText perk={p} />
              </li>
            ))}
            {next && !hideNextPerks && next.perks
              .filter((p) => !t.perks.some((x) => x.text === p.text))
              .slice(0, 2)
              .map((p, i) => (
                <li key={`locked-${i}`} className="is-locked" data-testid={`tier-perk-locked-${i}`}>
                  <Lock className="am-tier-perk-lock" />
                  <span>
                    <span className="am-tier-perk-strike">{p.text}</span>
                    {p.coming_soon && <span className="am-perk-soon">COMING SOON</span>}
                    <span className="am-tier-perk-at" style={{ color: nc }}>
                      &nbsp;at {next.label}
                    </span>
                  </span>
                </li>
              ))}
          </ul>
        </div>

        {/* ─── Balance strip ────────────────────────────────── */}
        <div className="am-tier-balance" data-testid="tier-points-balance">
          <span className="am-tier-balance-label">Points balance</span>
          <span className="am-tier-balance-value">
            {pointsBalance.toLocaleString()} pts
          </span>
        </div>
    </TierCardFrame>
  );
}

export default CustomerTierCard;
